import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getListAllDistData, getListSelectShopData } from '../../redux/digitalPriceSlice';
import './DigitalPriceTag.css';
import Button from '../../components/button';
import CustomPaginationTable from '../../components/table/CustomPaginationTable';
import { formatDatetime } from '../../utils/dateUtils';
import AddUploadForm from '../../components/customForm/AddUploadForm';

function LogPreviewForm({ title, onCancel, previewData }) {
  const dispatch = useDispatch();
  const { discountType } = previewData || {};
  const isSelectedShop = discountType === '3';

  const {
    resdata: previewItems,
    totalElements: totalRows,
    pageNumber: currentPage,
    loading,
    error
  } = useSelector((state) =>
    isSelectedShop
      ? state.digitalPrice.selectShopData
      : state.digitalPrice
  );

  const [perPage, setPerPage] = useState(10);
  const [searchParams, setSearchParams] = useState({
    sku: '',
    promoDate: null
  });
  const initialLoad = useRef(true);

  const buildQueryParams = () => {
    const { sku, promoDate } = searchParams;

    if (isSelectedShop) {
      return {
        page: currentPage,
        size: perPage,
        excelRecordId: previewData.excelRecordId,
        discountType: 3,
        isActive: true,
        shops: [],
        sku: sku || undefined,
        promoDate: promoDate ? formatDatetime(promoDate).substring(0, 10) : undefined,
        skipIsActive: true
      };
    } else {
      return {
        page: currentPage,
        size: perPage,
        excelRecordId: previewData.excelRecordId,
        sku: sku || undefined,
        promoDate: promoDate ? formatDatetime(promoDate).substring(0, 10) : undefined,
        skipIsActive: true
      };
    }
  };

  const handleSearchParamsChange = (field, value) => {
    setSearchParams(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSearch = () => {
    // Reset to first page when searching
    const params = buildQueryParams();
    dispatch(isSelectedShop
      ? getListSelectShopData({ ...params, page: 0 })
      : getListAllDistData({ ...params, page: 0 })
    );
  };

  const handleReset = () => {
    setSearchParams({
      sku: '',
      promoDate: null
    });
    // Reset to first page when resetting
    const params = buildQueryParams();
    dispatch(isSelectedShop
      ? getListSelectShopData({ ...params, page: 0, sku: '', promoDate: null })
      : getListAllDistData({ ...params, page: 0, sku: '', promoDate: null })
    );
  };

  useEffect(() => {
    if (previewData?.excelRecordId) {
      if (initialLoad.current) {
        const params = buildQueryParams();
        dispatch(isSelectedShop
          ? getListSelectShopData(params)
          : getListAllDistData(params)
        );
        initialLoad.current = false;
      }
    }
  }, [previewData?.excelRecordId, isSelectedShop]);

  const handlePageChange = (page) => {
    const params = buildQueryParams();
    dispatch(isSelectedShop
      ? getListSelectShopData({ ...params, page: page - 1 })
      : getListAllDistData({ ...params, page: page - 1 })
    );
  };

  const handlePerRowsChange = (newPerPage, page) => {
    setPerPage(newPerPage);
    const params = buildQueryParams();
    dispatch(isSelectedShop
      ? getListSelectShopData({ ...params, page: page - 1, size: newPerPage })
      : getListAllDistData({ ...params, page: page - 1, size: newPerPage })
    );
  };

  const getColumns = () => {
    const baseColumns = [
      {
        name: <div>Record ID</div>,
        cell: (row) => (
          <div className={row.isError ? "error-msg" : "non-error-msg"}>
            {row.discountDataId}
          </div>
        ),
      },
      {
        name: 'Stock Code',
        cell: (row) => (
          <div className={row.isError ? "error-msg" : "non-error-msg"}>
            {row.stockCode}
          </div>
        ),
      },
      {
        name: 'Brand Name',
        cell: (row) => (
          <div className={row.isError ? "error-msg" : "non-error-msg"}>
            {row.brandName}
          </div>
        ),
      },
      {
        name: <div>Model Name</div>,
        cell: (row) => (
          <div className={row.isError ? "error-msg" : "non-error-msg"}>
            {row.modelName}
          </div>
        ),
      },
      {
        name: <div>Discount</div>,
        cell: (row) => (
          <div className={row.isError ? "error-msg" : "non-error-msg"}>
            {row.discountAmount !== null && row.discountAmount !== undefined
              ? parseFloat(row.discountAmount).toFixed(2)
              : ''}
          </div>
        ),
      },
      {
        name: <div>Promo RRP</div>,
        cell: (row) => (
          <div className={row.isError ? "error-msg" : "non-error-msg"}>
            {row.promoRrp !== null && row.promoRrp !== undefined
              ? parseFloat(row.promoRrp).toFixed(2)
              : ''}
          </div>
        ),
      },
      {
        name: <div>Promo Start Date</div>,
        cell: (row) => (
          <div className={row.isError ? "error-msg" : "non-error-msg"}>
            {row.periodStart ? row.periodStart.substring(0, 10) : null}
          </div>
        ),
      },
      {
        name: <div>Promo End Date</div>,
        cell: (row) => (
          <div className={row.isError ? "error-msg" : "non-error-msg"}>
            {row.periodEnd ? row.periodEnd.substring(0, 10) : null}
          </div>
        ),
      }
    ];

    // Conditional columns that should appear before Uploaded By
    const conditionalColumns = [];

    // For Roadshow (type '1'), show Freebies but hide shop columns
    if (previewData?.discountType === '1') {
      conditionalColumns.push({
        name: <div>Freebies</div>,
        cell: (row) => (
          <div className={row.isError ? "error-msg" : "non-error-msg"}>
            {row.freebies}
          </div>
        ),
      });
    }
    // For Selected Shop (type '3'), show shop columns but hide Freebies
    else if (previewData?.discountType === '3') {
      conditionalColumns.push(
        {
          name: <div>Selected Singtel Shop(s)</div>,
          cell: (row) => <div className={row.isError ? "error-msg" : "non-error-msg"}>{row.singtelShopNames}</div>,
        },
        {
          name: <div>Selected SER Shop(s)</div>,
          cell: (row) => <div className={row.isError ? "error-msg" : "non-error-msg"}>{row.partnerShopNames}</div>,
        }
      );
    }
    // For default case, show all columns
    else if (!previewData?.discountType || previewData?.discountType === '') {
      conditionalColumns.push(
        {
          name: <div>Selected Singtel Shop(s)</div>,
          cell: (row) => <div className={row.isError ? "error-msg" : "non-error-msg"}>{row.singtelShopNames}</div>,
        },
        {
          name: <div>Selected SER Shop(s)</div>,
          cell: (row) => <div className={row.isError ? "error-msg" : "non-error-msg"}>{row.partnerShopNames}</div>,
        },
        {
          name: <div>Freebies</div>,
          cell: (row) => (
            <div className={row.isError ? "error-msg" : "non-error-msg"}>
              {row.freebies}
            </div>
          ),
        }
      );
    }

    // Remaining fixed columns
    const remainingColumns = [
      {
        name: <div>Uploaded By</div>,
        cell: (row) => (
          <div className={row.isError ? "error-msg" : "non-error-msg"}>
            {formatDatetime(row.createdBy)}
          </div>
        ),
      },
      {
        name: <div>Uploaded Date Time</div>,
        cell: (row) => (
          <div className={row.isError ? "error-msg" : "non-error-msg"}>
            {formatDatetime(row.createdDatetime)}
          </div>
        ),
      },
      {
        name: <div>Error Message</div>,
        cell: (row) => (
          <div className={row.isError ? "error-msg" : "non-error-msg"}>
            {row.messageText}
          </div>
        ),
      }
    ];

    return [...baseColumns, ...conditionalColumns, ...remainingColumns];
  };

  return (
    <div className="manage-user-container">
      <div style={{ padding: '30px' }}>
        {title}
        <div style={{ display: 'flex', width: '750px', alignItems: 'center' }}>
          <AddUploadForm
            onSearchParamsChange={handleSearchParamsChange}
            searchParams={searchParams}
          />
          <div style={{ marginLeft: 'auto', display: 'flex', marginTop: '35px' }}>
            <Button className='upload-btn' style={{ marginRight: '20px' }} onClick={handleSearch}>
              SEARCH
            </Button>
            <Button className='upload-btn' onClick={handleReset}>
              RESET
            </Button>
          </div>
        </div>
        <div style={{ marginTop: '20px' }}>
          <CustomPaginationTable
            columns={getColumns()}
            data={previewItems}
            totalRows={totalRows}
            loading={loading}
            perPage={perPage}
            currentPage={currentPage + 1}
            onPageChange={handlePageChange}
            onPerRowsChange={handlePerRowsChange}
          />
        </div>
        <div className="close-button">
          <Button className='upload-btn' onClick={onCancel}>
            CLOSE
          </Button>
        </div>
      </div>
    </div>
  );
}

export default LogPreviewForm;
